import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckCircleIcon, Clear as ClearIcon } from '@mui/icons-material';

interface PetitMinBetaProps {
  open: boolean;
  onClose: () => void;
  couloirsPetiteMine?: any; // GeoJSON FeatureCollection
  existingPolygons?: any; // Existing applications to check overlap
}

interface FormData {
  // Company Information
  societe: string;
  nif: string;
  nni: string;
  
  // Responsible Person Information
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  
  // Coordinates and datum
  datum: 'UTM28' | 'UTM29';
  selectedCorridor: string;
  coordinates: Array<{ x: string; y: string }>;
}

const steps = ['Informations Société', 'Personne Responsable', 'Coordonnées'];

const PetitMinBeta: React.FC<PetitMinBetaProps> = ({ 
  open, 
  onClose, 
  couloirsPetiteMine,
  existingPolygons 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    societe: '',
    nif: '',
    nni: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    datum: 'UTM28',
    selectedCorridor: '',
    coordinates: [
      { x: '', y: '' },
      { x: '', y: '' },
      { x: '', y: '' },
      { x: '', y: '' }
    ]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [couloirs, setCouloirs] = useState<any>(null);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    const loadCouloirs = async () => {
      try {
        const response = await fetch('/data/test-couloirs-petiteMine.json');
        if (response.ok) {
          const data = await response.json();
          setCouloirs(data);
          console.log('Loaded couloirs:', data.features);
        }
      } catch (error) {
        console.error('Error loading couloirs:', error);
      }
    };

    if (open) {
      loadCouloirs();
    }
  }, [open]);

  // Corridor to Datum mapping
  const corridorDatumMapping: Record<string, 'UTM28' | 'UTM29'> = {
    'Couloir Gleib Jrad': 'UTM28',
    'Couloir Sfériat': 'UTM29',
    'Couloir Graret Senein': 'UTM28',
    'Couloir Guetaa Dhweyatt': 'UTM28',
    'Couloir TAMAYA': 'UTM28',
    'Couloir Tijirit PM': 'UTM28',
    'Couloir Agane PM': 'UTM28',
    'Couloir Wedyane El Kharoub': 'UTM29'
  };

  // Fallback corridor list if prop data is not available
  const fallbackCorridors = [
    { name: 'Couloir Gleib Jrad', type: 'Petite mine', area_km2: 285 },
    { name: 'Couloir Sfériat', type: 'Mixte', area_km2: 1160 },
    { name: 'Couloir Graret Senein', type: 'Mixte', area_km2: 47 },
    { name: 'Couloir Guetaa Dhweyatt', type: 'Mixte', area_km2: 1617 },
    { name: 'Couloir TAMAYA', type: 'Mixte', area_km2: 381 },
    { name: 'Couloir Tijirit PM', type: 'Petite mine', area_km2: 344 },
    { name: 'Couloir Agane PM', type: 'Petite mine', area_km2: 3290 },
    { name: 'Couloir Wedyane El Kharoub', type: 'Petite mine', area_km2: 8477 }
  ];

  // Get corridors from loaded data or use fallback
  const getCorridors = () => {
    if (couloirs?.features && couloirs.features.length > 0) {
      return couloirs.features.map((f: any) => ({
        name: f.properties.name || f.properties.Nom || 'Unknown',
        type: f.properties.type || f.properties['Type d\'activité'] || 'Unknown',
        area_km2: f.properties.area_km2 || f.properties.Superficie || 0
      }));
    }
    console.warn('Using fallback corridor list - couloirs data not loaded yet');
    return fallbackCorridors;
  };

  const availableCorridors = getCorridors();

  // COMMENTED OUT: Authentication is disabled
  // Get auth token
  // const getAuthToken = () => {
  //   return localStorage.getItem('authToken');
  // };

  // Handle form input changes
  const handleInputChange = (field: keyof FormData, value: string) => {
    if (field === 'selectedCorridor') {
      // Ensure the value starts with "Couloir " for backend validation
      const fullCorridorName = value.startsWith('Couloir ') ? value : `Couloir ${value}`;
      
      // Auto-set datum based on corridor selection and clear coordinates
      const datum = corridorDatumMapping[fullCorridorName] || 'UTM28';
      
      console.log('Selected corridor (UI):', value);
      console.log('Full corridor name (backend):', fullCorridorName);
      
      setFormData(prev => ({
        ...prev,
        selectedCorridor: fullCorridorName, // Store the full name internally
        datum: datum,
        coordinates: [
          { x: '', y: '' },
          { x: '', y: '' },
          { x: '', y: '' },
          { x: '', y: '' }
        ]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handle coordinate changes
  const handleCoordinateChange = (index: number, field: 'x' | 'y', value: string) => {
    setFormData(prev => ({
      ...prev,
      coordinates: prev.coordinates.map((coord, i) =>
        i === index ? { ...coord, [field]: value } : coord
      )
    }));
  };

  // Convert coordinates based on datum
  const convertToLatLon = (x: number, y: number, datum: string) => {
    // UTM to Lat/Lon conversion (simplified - you should use a proper library like proj4js)
    const zone = datum === 'UTM28' ? 28 : 29;
    
    // Simplified conversion (replace with proj4js)
    const centralMeridian = (zone - 1) * 6 - 180 + 3;
    const lat = y / 111320; // Approximate
    const lon = centralMeridian + (x - 500000) / (111320 * Math.cos(lat * Math.PI / 180));
    
    return [lon, lat];
  };

  // Validate coordinates with strict rules
  const validateCoordinates = (): { valid: boolean; error?: string; coordinates?: number[][]; validPointsCount: number } => {
    let validPointsCount = 0;
    const coordinates: number[][] = [];
    let hasAnyInput = false; // Track if user has started entering coordinates
    
    for (let i = 0; i < formData.coordinates.length; i++) {
      const coord = formData.coordinates[i];
      
      // Check if user has started entering coordinates
      if (coord.x || coord.y) {
        hasAnyInput = true;
      }
      
      // Skip empty coordinates
      if (!coord.x || !coord.y) {
        continue;
      }
      
      const x = parseFloat(coord.x);
      const y = parseFloat(coord.y);
      
      // Check if values are valid numbers
      if (isNaN(x) || isNaN(y)) {
        continue;
      }
      
      // Check if they are integers (no decimals)
      if (x !== Math.floor(x) || y !== Math.floor(y)) {
        continue;
      }
      
      // Check if they end with exactly three zeros
      if (x % 1000 !== 0 || y % 1000 !== 0) {
        continue;
      }
      
      // UTM validation ranges
      if (x < 100000 || x > 900000) {
        continue;
      }
      if (y < 1000000 || y > 4000000) {
        continue;
      }
      
      // If we reach here, the point is valid
      validPointsCount++;
      
      // Convert to lat/lon for GeoJSON compatibility
      const [lon, lat] = convertToLatLon(x, y, formData.datum);
      coordinates.push([lon, lat]);
    }
    
    // Only show error if user has started entering coordinates AND has insufficient valid points
    if (hasAnyInput && validPointsCount < 3) {
      return { 
        valid: false, 
        error: 'Au moins 3 points valides sont requis. Chaque coordonnée doit être un nombre entier se terminant par trois zéros (ex: 433000).', 
        validPointsCount 
      };
    }
    
    // If no input has been started, consider it valid (no error message)
    if (!hasAnyInput) {
      return { valid: false, coordinates, validPointsCount }; // Invalid for submission but no error message
    }
    
    return { valid: true, coordinates, validPointsCount };
  };

  // Calculate area (UTM-aware)
  const calculateArea = (coordinates: number[][], isUTM: boolean = false) => {
    let area = 0;
    const n = coordinates.length;
    
    if (isUTM) {
      // Direct UTM calculation in meters
      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += coordinates[i][0] * coordinates[j][1];
        area -= coordinates[j][0] * coordinates[i][1];
      }
      area = Math.abs(area) / 2;
      return area / 1000000; // Convert m² to km²
    } else {
      // Lat/Lon calculation with proper projection
      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += coordinates[i][0] * coordinates[j][1];
        area -= coordinates[j][0] * coordinates[i][1];
      }
      area = Math.abs(area) / 2;
      
      // Convert to square kilometers using proper projection
      const avgLat = coordinates.reduce((sum, coord) => sum + coord[1], 0) / n;
      const latFactor = Math.cos(avgLat * Math.PI / 180);
      return area * 111 * 111 * latFactor * latFactor;
    }
  };

  // Point in polygon test
  const pointInPolygon = (point: number[], polygon: number[][]) => {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  };

  // Check if polygon is inside selected corridor
  const validateInsideSelectedCorridor = (coordinates: number[][]) => {
    if (!couloirs || !couloirs.features || !formData.selectedCorridor) {
      return { valid: false, error: 'Aucun couloir sélectionné ou données de couloir indisponibles' };
    }

    console.log('Validating corridor:', formData.selectedCorridor);
    console.log('Available corridors:', couloirs.features.map((f: any) => f.properties.name));

    // Find the selected corridor - match exactly with properties.name
    const selectedCorridor = couloirs.features.find((f: any) => {
      const match = f.properties.name === formData.selectedCorridor;
      console.log(`Comparing "${f.properties.name}" with "${formData.selectedCorridor}": ${match}`);
      return match;
    });

    if (!selectedCorridor) {
      console.error('Corridor not found. Available:', couloirs.features.map((f: any) => f.properties.name));
      return { 
        valid: false, 
        error: `Le couloir "${formData.selectedCorridor}" n'a pas été trouvé dans les données disponibles.` 
      };
    }

    console.log('Found matching corridor:', selectedCorridor.properties.name);

    // Check if all points are inside the selected corridor
    const corridorCoords = selectedCorridor.geometry.coordinates[0];
    
    for (let i = 0; i < coordinates.length; i++) {
      const point = coordinates[i];
      if (!pointInPolygon(point, corridorCoords)) {
        return { 
          valid: false, 
          error: `Le Point ${i + 1} n'est pas à l'intérieur du couloir sélectionné "${formData.selectedCorridor}"` 
        };
      }
    }

    return { valid: true, corridor: selectedCorridor };
  };

  // Check overlap with existing polygons
  const validateNoOverlap = (coordinates: number[][]) => {
    if (!existingPolygons || !existingPolygons.features) {
      return { valid: true }; // No existing polygons to check
    }

    // Simple overlap check - you might want to use a more sophisticated algorithm
    for (const existing of existingPolygons.features) {
      const existingCoords = existing.geometry.coordinates[0];
      
      // Check if any point of new polygon is inside existing polygon
      for (const point of coordinates) {
        if (pointInPolygon(point, existingCoords)) {
          return { 
            valid: false, 
            error: `Polygon overlaps with existing application: ${existing.properties?.ID || 'Unknown'}` 
          };
        }
      }
    }

    return { valid: true };
  };

  // Update the area validation with strict 2.00 km² requirement
  const validateArea = (coordinates: number[][]): { valid: boolean; error?: string; area: number } => {
    // Check if user has started entering any coordinates
    const hasAnyInput = formData.coordinates.some(coord => coord.x || coord.y);
    
    if (!hasAnyInput) {
      return { valid: false, area: 0 }; // Invalid for submission but no error message
    }
    
    if (coordinates.length < 3) {
      return { valid: false, error: 'Points insuffisants pour calculer la surface', area: 0 };
    }
    
    // Calculate area using UTM method
    const utmCoords = formData.coordinates
      .filter(c => c.x && c.y)
      .map(c => [parseFloat(c.x), parseFloat(c.y)]);
    
    const area = calculateArea(utmCoords, true);
    
    // Strict validation: exactly 2.00 km² (no tolerance)
    if (Math.abs(area - 2.0) > 0.001) { // Using tiny tolerance for floating point precision
      return { 
        valid: false, 
        error: 'Le périmètre ne peut pas dépasser 2 km²', 
        area 
      };
    }
    
    return { valid: true, area };
  };

  // Clear all coordinates
  const handleClearAll = () => {
    setFormData(prev => ({
      ...prev,
      coordinates: [
        { x: '', y: '' },
        { x: '', y: '' },
        { x: '', y: '' },
        { x: '', y: '' }
      ]
    }));
  };

  // Clear individual coordinate row
  const handleClearRow = (index: number) => {
    setFormData(prev => ({
      ...prev,
      coordinates: prev.coordinates.map((coord, i) =>
        i === index ? { x: '', y: '' } : coord
      )
    }));
  };

  // Submit form
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate coordinates
      const coordValidation = validateCoordinates();
      if (!coordValidation.valid) {
        setError(coordValidation.error || 'Coordinate validation failed');
        setLoading(false);
        return;
      }

      if (!coordValidation.coordinates) {
        setError('No valid coordinates found');
        setLoading(false);
        return;
      }

      const coordinates = coordValidation.coordinates;
      
      // Calculate area using UTM method (always UTM now)
      const area = calculateArea(
        formData.coordinates.filter(c => c.x && c.y).map(c => [parseFloat(c.x), parseFloat(c.y)]),
        true // Always UTM
      );

      // Check if area is exactly 2 km²
      if (Math.abs(area - 2.0) > 0.1) {
        setError(`Area must be exactly 2 km² (±0.1). Current area: ${area.toFixed(2)} km²`);
        setLoading(false);
        return;
      }

      // Validate inside selected corridor
      const corridorValidation = validateInsideSelectedCorridor(coordinates);
      if (!corridorValidation.valid) {
        setError(corridorValidation.error || 'Corridor validation failed');
        setLoading(false);
        return;
      }

      // Validate no overlap
      const overlapValidation = validateNoOverlap(coordinates);
      if (!overlapValidation.valid) {
        setError(overlapValidation.error || 'Overlap validation failed');
        setLoading(false);
        return;
      }

      // COMMENTED OUT: Authentication is disabled
      // const token = getAuthToken();
      // if (!token) {
      //   throw new Error('Authentication required');
      // }

      // Submit to backend
      const response = await fetch('/api/petitmin/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // COMMENTED OUT: No authentication token needed
          // 'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          coordinates: coordinates,
          datum: formData.datum,
          originalCoordinates: formData.coordinates.filter(c => c.x && c.y),
          selectedCorridor: formData.selectedCorridor, // This will be the full "Couloir ..." name
          properties: {
            Societe: formData.societe,
            NIF: formData.nif,
            NNI: formData.nni,
            Nom: formData.nom,
            Prénom: formData.prenom,
            Email: formData.email,
            Telephone: formData.telephone,
            Area: `${area.toFixed(2)} km²`,
            Datum: formData.datum,
            Couloir: formData.selectedCorridor // Full name for backend
          }
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Submission failed');
      }

      setSuccess(true);
      setValidationResult(result);
      setActiveStep(3); // Show success step

    } catch (err: any) {
      setError(err.message || 'Submission error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Handle step navigation
  const handleNext = () => {
    if (activeStep === 2) {
      handleSubmit();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      societe: '',
      nif: '',
      nni: '',
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      datum: 'UTM28',
      selectedCorridor: '',
      coordinates: [
        { x: '', y: '' },
        { x: '', y: '' },
        { x: '', y: '' },
        { x: '', y: '' }
      ]
    });
    setError(null);
    setSuccess(false);
    setValidationResult(null);
    onClose();
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{
              fontFamily: 'Louguiya, sans-serif',
              fontSize: '1.1rem',
              color: '#2c8fdb',
              mb: 2
            }}>
              🏢 Informations de la Société
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nom de la Société *"
                  value={formData.societe}
                  onChange={(e) => handleInputChange('societe', e.target.value)}
                  required
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: 'rgba(255,255,255,0.7)'
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.9rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2c8fdb'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#2c8fdb'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="NIF *"
                  value={formData.nif}
                  onChange={(e) => handleInputChange('nif', e.target.value)}
                  required
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: 'rgba(255,255,255,0.7)'
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.9rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2c8fdb'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#2c8fdb'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="NNI *"
                  value={formData.nni}
                  onChange={(e) => handleInputChange('nni', e.target.value)}
                  required
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: 'rgba(255,255,255,0.7)'
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.9rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2c8fdb'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#2c8fdb'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{
              fontFamily: 'Louguiya, sans-serif',
              fontSize: '1.1rem',
              color: '#2c8fdb',
              mb: 2
            }}>
              👤 Personne Responsable
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nom *"
                  value={formData.nom}
                  onChange={(e) => handleInputChange('nom', e.target.value)}
                  required
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: 'rgba(255,255,255,0.7)'
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.9rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2c8fdb'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#2c8fdb'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Prénom *"
                  value={formData.prenom}
                  onChange={(e) => handleInputChange('prenom', e.target.value)}
                  required
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: 'rgba(255,255,255,0.7)'
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.9rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2c8fdb'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#2c8fdb'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: 'rgba(255,255,255,0.7)'
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.9rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2c8fdb'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#2c8fdb'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Téléphone *"
                  value={formData.telephone}
                  onChange={(e) => handleInputChange('telephone', e.target.value)}
                  required
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: 'rgba(255,255,255,0.7)'
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.9rem',
                      fontFamily: 'Louguiya, sans-serif',
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2c8fdb'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#2c8fdb'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        // Real-time validation
        const coordValidation = validateCoordinates();
        const validCoords = formData.coordinates.filter(coord => {
          if (!coord.x || !coord.y) return false;
          
          const x = parseFloat(coord.x);
          const y = parseFloat(coord.y);
          
          // Must be valid numbers
          if (isNaN(x) || isNaN(y)) return false;
          
          // Must be integers
          if (x !== Math.floor(x) || y !== Math.floor(y)) return false;
          
          // Must end with three zeros
          if (x % 1000 !== 0 || y % 1000 !== 0) return false;
          
          // Must be in UTM range
          if (x < 100000 || x > 900000) return false;
          if (y < 1000000 || y > 4000000) return false;
          
          return true;
        });
        
        const areaValidation = validateArea(coordValidation.coordinates || []);
        const area = areaValidation.area;
        
        // Determine if form is valid for submission
        const isFormValid = coordValidation.valid && areaValidation.valid && formData.selectedCorridor;
        
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{
              fontFamily: 'Louguiya, sans-serif',
              fontSize: '1.1rem',
              color: '#2c8fdb',
              mb: 2
            }}>
              📍 Coordonnées du Polygone
            </Typography>
            
            {/* Corridor Selection - keep existing code */}
            <Grid container spacing={1} sx={{ mb: 1.5 }}>
              <Grid item xs={12}>
                <FormControl 
                  fullWidth 
                  required
                  size="small"
                  sx={{ 
                    '& .MuiInputLabel-root': { 
                      fontSize: '0.85rem',
                      fontFamily: 'Louguiya, sans-serif'
                    },
                    '& .MuiSelect-select': {
                      fontSize: '0.85rem',
                      fontFamily: 'Louguiya, sans-serif',
                      py: 1
                    }
                  }}
                >
                  <InputLabel sx={{ color: '#fff' }}>Sélectionner le Couloir *</InputLabel>
                  <Select
                    value={formData.selectedCorridor.replace('Couloir ', '')} // Display short name in UI
                    label="Sélectionner le Couloir *"
                    onChange={(e) => handleInputChange('selectedCorridor', e.target.value)}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: '#2a2d3a',
                          border: '1px solid rgba(255,255,255,0.12)',
                          maxHeight: 300,
                          '& .MuiMenuItem-root': {
                            fontSize: '0.85rem',
                            fontFamily: 'Louguiya, sans-serif',
                            color: '#fff',
                            py: 0.5,
                            '&:hover': {
                              backgroundColor: 'rgba(44, 143, 219, 0.2)'
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(44, 143, 219, 0.3)'
                            }
                          }
                        }
                      }
                    }}
                    sx={{
                      color: '#fff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.3)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.5)'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2c8fdb'
                      }
                    }}
                  >
                    {couloirs?.features?.map((corridor: any) => (
                      <MenuItem key={corridor.properties.name} value={corridor.properties.name.replace('Couloir ', '')}>
                        {corridor.properties.name.replace('Couloir ', '')}
                      </MenuItem>
                    )) || fallbackCorridors.map((corridor: { name: string; type: string; area_km2: number }) => (
                      <MenuItem key={corridor.name} value={corridor.name.replace('Couloir ', '')}>
                        {corridor.name.replace('Couloir ', '')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Only show content if a corridor is selected */}
            {formData.selectedCorridor && (
              <>
                {/* Updated Status Panel with real-time validation */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                  <Paper sx={{ 
                    px: 1, 
                    py: 0.4,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    width: '65%'
                  }}>
                    <Grid container spacing={0.5}>
                      <Grid item xs={4}>
                        <Box textAlign="center">
                          <Typography variant="caption" sx={{ 
                            color: 'rgba(255,255,255,0.7)', 
                            fontSize: '0.5rem',
                            fontFamily: 'Louguiya, sans-serif',
                            display: 'block',
                            lineHeight: 1
                          }}>
                            Surface
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: areaValidation.valid ? '#4caf50' : '#f44336',
                            fontSize: '0.7rem',
                            fontFamily: 'Louguiya, sans-serif',
                            fontWeight: 600,
                            mt: 0.1
                          }}>
                            {area.toFixed(2)} km²
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box textAlign="center">
                          <Typography variant="caption" sx={{ 
                            color: 'rgba(255,255,255,0.7)', 
                            fontSize: '0.5rem',
                            fontFamily: 'Louguiya, sans-serif',
                            display: 'block',
                            lineHeight: 1
                          }}>
                            Valid Points
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: coordValidation.validPointsCount >= 3 ? '#4caf50' : '#f44336',
                            fontSize: '0.7rem',
                            fontFamily: 'Louguiya, sans-serif',
                            fontWeight: 600,
                            mt: 0.1
                          }}>
                            {coordValidation.validPointsCount}/4
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box textAlign="center">
                          <Typography variant="caption" sx={{ 
                            color: 'rgba(255,255,255,0.7)', 
                            fontSize: '0.5rem',
                            fontFamily: 'Louguiya, sans-serif',
                            display: 'block',
                            lineHeight: 1
                          }}>
                            UTM Zone
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: formData.selectedCorridor ? '#4caf50' : 'rgba(255,255,255,0.5)',
                            fontSize: '0.7rem',
                            fontFamily: 'Louguiya, sans-serif',
                            fontWeight: 600,
                            mt: 0.1
                          }}>
                            {formData.selectedCorridor ? formData.datum.replace('UTM', '') : '--'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>

                {/* Instructions and Clear All Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ 
                    color: 'rgba(255,255,255,0.8)', 
                    fontSize: '0.75rem',
                    fontFamily: 'Louguiya, sans-serif'
                  }}>
                    Entrez les sommets du polygone
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button
                      size="small"
                      onClick={() => setShowInstructions(!showInstructions)}
                      sx={{
                        fontSize: '0.7rem',
                        fontFamily: 'Louguiya, sans-serif',
                        color: '#2c8fdb',
                        textTransform: 'none',
                        minWidth: 'auto',
                        px: 1,
                        py: 0.2,
                        '&:hover': {
                          backgroundColor: 'rgba(44, 143, 219, 0.1)'
                        }
                      }}
                    >
                      {showInstructions ? '▲ Instructions' : '▼ Instructions'}
                    </Button>
                    <Button
                      size="small"
                      onClick={handleClearAll}
                      sx={{
                        fontSize: '0.6rem',
                        fontFamily: 'Louguiya, sans-serif',
                        color: '#f44336',
                        textTransform: 'none',
                        minWidth: 'auto',
                        px: 0.8,
                        py: 0.2,
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.1)'
                        }
                      }}
                    >
                      <ClearIcon sx={{ fontSize: '0.8rem', mr: 0.3 }} />
                      Effacer tout
                    </Button>
                  </Box>
                </Box>

                {/* Updated Instructions */}
                {showInstructions && (
                  <Paper sx={{
                    p: 0.6,
                    mb: 1.5,
                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                    border: '1px solid rgba(33, 150, 243, 0.2)',
                    borderRadius: 1
                  }}>
                    <Typography variant="body2" sx={{ 
                      fontSize: '0.85rem', // Same as the dropdown label
                      fontFamily: 'Louguiya, sans-serif',
                      lineHeight: 1.2,
                      color: 'rgba(255,255,255,0.65)'
                    }}>
                      • Les coordonnées doivent respecter le quadrillage cadastral (ex : 433000, sans décimales).<br/>
                      • Le périmètre doit être exactement de 2,00 km², correspondant à deux unités cadastrales.<br/>
                      • Le périmètre doit être situé intégralement dans le couloir sélectionné.
                    </Typography>
                  </Paper>
                )}

                {/* Enhanced coordinate inputs with clear buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ width: '85%' }}>
                    {formData.coordinates.map((coord, index) => (
                      <Grid container spacing={0.8} key={index} alignItems="center" sx={{ mb: 0.5 }}>
                        <Grid item xs={2.2}>
                          <Typography variant="subtitle2" sx={{ 
                            color: '#2c8fdb',
                            fontSize: '0.65rem',
                            fontFamily: 'Louguiya, sans-serif',
                            fontWeight: 600,
                            textAlign: 'right',
                            pr: 0.5
                          }}>
                            Point {index + 1}
                          </Typography>
                        </Grid>
                        <Grid item xs={4.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Easting (X)"
                            value={coord.x}
                            onChange={(e) => handleCoordinateChange(index, 'x', e.target.value)}
                            placeholder={formData.datum === 'UTM28' ? '433000' : '280000'}
                            sx={{
                              '& .MuiInputLabel-root': {
                                fontSize: '0.6rem',
                                fontFamily: 'Louguiya, sans-serif',
                                color: 'rgba(255,255,255,0.7)'
                              },
                              '& .MuiOutlinedInput-root': {
                                fontSize: '0.7rem',
                                fontFamily: 'Louguiya, sans-serif',
                                color: '#fff',
                                backgroundColor: 'rgba(255,255,255,0.02)',
                                borderRadius: '6px',
                                '& fieldset': {
                                  borderColor: 'rgba(255,255,255,0.25)',
                                  borderWidth: '1px'
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(255,255,255,0.4)'
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#2c8fdb',
                                  borderWidth: '1.5px'
                                },
                                '& input': {
                                  padding: '6px 8px'
                                }
                              },
                              '& .MuiInputLabel-root.Mui-focused': {
                                color: '#2c8fdb'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={4.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Northing (Y)"
                            value={coord.y}
                            onChange={(e) => handleCoordinateChange(index, 'y', e.target.value)}
                            placeholder={formData.datum === 'UTM28' ? '2300000' : '2350000'}
                            sx={{
                              '& .MuiInputLabel-root': {
                                fontSize: '0.6rem',
                                fontFamily: 'Louguiya, sans-serif',
                                color: 'rgba(255,255,255,0.7)'
                              },
                              '& .MuiOutlinedInput-root': {
                                fontSize: '0.7rem',
                                fontFamily: 'Louguiya, sans-serif',
                                color: '#fff',
                                backgroundColor: 'rgba(255,255,255,0.02)',
                                borderRadius: '6px',
                                '& fieldset': {
                                  borderColor: 'rgba(255,255,255,0.25)',
                                  borderWidth: '1px'
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(255,255,255,0.4)'
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#2c8fdb',
                                  borderWidth: '1.5px'
                                },
                                '& input': {
                                  padding: '6px 8px'
                                }
                              },
                              '& .MuiInputLabel-root.Mui-focused': {
                                color: '#2c8fdb'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={0.8}>
                          <Button
                            size="small"
                            onClick={() => handleClearRow(index)}
                            disabled={!coord.x && !coord.y}
                            sx={{
                              minWidth: '24px',
                              width: '24px',
                              height: '24px',
                              p: 0,
                              color: '#f44336',
                              opacity: (!coord.x && !coord.y) ? 0.3 : 0.7,
                              '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                opacity: 1
                              },
                              '&:disabled': {
                                color: '#f44336',
                                opacity: 0.3
                              }
                            }}
                          >
                            <ClearIcon sx={{ fontSize: '0.9rem' }} />
                          </Button>
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                </Box>

                {/* Real-time validation errors - only show specific errors */}
                {(() => {
                  const hasAnyInput = formData.coordinates.some(coord => coord.x || coord.y);
                  return hasAnyInput && (!coordValidation.valid || !areaValidation.valid) && areaValidation.error;
                })() && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mt: 1.5,
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      border: '1px solid rgba(244, 67, 54, 0.3)',
                      borderRadius: 2,
                      '& .MuiAlert-message': {
                        fontSize: '0.7rem',
                        fontFamily: 'Louguiya, sans-serif'
                      },
                      '& .MuiAlert-icon': {
                        fontSize: '1rem'
                      }
                    }}
                  >
                    {areaValidation.error}
                  </Alert>
                )}
              </>
            )}
          </Box>
        );

      case 3:
        return (
          <Box textAlign="center">
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Demande soumise avec succès!
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Votre demande a été validée et enregistrée dans le système.
            </Typography>
            {validationResult && (
              <Paper sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Détails de la soumission:
                </Typography>
                <Typography variant="body2">
                  ID: {validationResult.feature?.properties?.ID_demande}<br/>
                  Société: {formData.societe}<br/>
                  Couloir: {formData.selectedCorridor}<br/>
                  Surface: {validationResult.feature?.properties?.Area}<br/>
                  Datum: {formData.datum}
                </Typography>
              </Paper>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(24,29,38,0.98) 0%, rgba(30,35,45,0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          '& .MuiDialog-paper': {
            margin: '16px'
          }
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(4px)'
        },
        zIndex: 1300
      }}
    >
      <DialogTitle sx={{ 
        color: '#fff', 
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 2,
        px: 3
      }}>
        <Typography variant="h5" sx={{ 
          fontFamily: 'Louguiya, sans-serif',
          fontWeight: 600,
          fontSize: '1.3rem',
          background: 'linear-gradient(135deg, #2c8fdb 0%, #4fc3f7 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          ⛏️ Petit Min Beta
        </Typography>
        <Button
          onClick={handleClose}
          size="small"
          sx={{ 
            color: 'rgba(255,255,255,0.7)', 
            minWidth: 'auto',
            borderRadius: '8px',
            p: 1,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: '#fff'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ 
        color: '#fff',
        px: 3,
        py: 2,
        '&::-webkit-scrollbar': {
          width: '6px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(255,255,255,0.1)'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(255,255,255,0.3)',
          borderRadius: '3px'
        }
      }}>
        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            mb: 3, 
            '& .MuiStepLabel-root': { 
              color: '#fff' 
            },
            '& .MuiStepLabel-label': {
              fontSize: '0.85rem',
              fontFamily: 'Louguiya, sans-serif',
              fontWeight: 500
            },
            '& .MuiStepIcon-root': {
              fontSize: '1.2rem'
            },
            '& .MuiStepIcon-root.Mui-active': {
              color: '#2c8fdb'
            },
            '& .MuiStepIcon-root.Mui-completed': {
              color: '#4caf50'
            }
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mt: 2,
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontSize: '0.85rem',
                fontFamily: 'Louguiya, sans-serif'
              },
              '& .MuiAlert-icon': {
                fontSize: '1.1rem'
              }
            }}
          >
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        borderTop: '1px solid rgba(255,255,255,0.1)',
        px: 3,
        py: 2,
        gap: 1,
        backgroundColor: 'rgba(255,255,255,0.02)'
      }}>
        {activeStep < 3 && (
          <>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0 || loading}
              size="small"
              sx={{
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontFamily: 'Louguiya, sans-serif',
                px: 2,
                py: 0.8,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderColor: 'rgba(255,255,255,0.5)'
                }
              }}
            >
              Retour
            </Button>
            <Button
              onClick={handleNext}
              disabled={loading || 
                (activeStep === 0 && (!formData.societe || !formData.nif || !formData.nni)) ||
                (activeStep === 1 && (!formData.nom || !formData.prenom || !formData.email || !formData.telephone)) ||
                (activeStep === 2 && (() => {
                  const coordValidation = validateCoordinates();
                  const areaValidation = validateArea(coordValidation.coordinates || []);
                  // Ensure corridor is selected (full name should exist)
                  return !formData.selectedCorridor || !formData.selectedCorridor.startsWith('Couloir ') || !coordValidation.valid || !areaValidation.valid;
                })())
              }
              variant="contained"
              size="small"
              sx={{
                backgroundColor: '#2c8fdb',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontFamily: 'Louguiya, sans-serif',
                fontWeight: 600,
                px: 2.5,
                py: 0.8,
                boxShadow: '0 4px 12px rgba(44, 143, 219, 0.3)',
                '&:hover': {
                  backgroundColor: '#1e6bb8',
                  boxShadow: '0 6px 16px rgba(44, 143, 219, 0.4)'
                },
                '&:disabled': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  boxShadow: 'none'
                }
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : activeStep === 2 ? (
                'Soumettre'
              ) : (
                'Suivant'
              )}
            </Button>
          </>
        )}
        
        {activeStep === 3 && (
          <Button
            onClick={handleClose}
            variant="contained"
            size="small"
            sx={{
              backgroundColor: '#2c8fdb',
              borderRadius: '8px',
              fontSize: '0.85rem',
              fontFamily: 'Louguiya, sans-serif',
              fontWeight: 600,
              px: 2.5,
              py: 0.8,
              boxShadow: '0 4px 12px rgba(44, 143, 219, 0.3)',
              '&:hover': {
                backgroundColor: '#1e6bb8',
                boxShadow: '0 6px 16px rgba(44, 143, 219, 0.4)'
              }
            }}
          >
            Fermer
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PetitMinBeta;